import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Button, Stack } from '@mui/material';
import CitationCard from './CitationCard';

export interface CitationRef {
  id: number;
}

interface CitationListProps {
  articles: CitationRef[];
  /** How many rows to show before collapsing the rest behind "+N more". */
  limit?: number;
  testId?: string;
}

/**
 * The sources strip, capped (PA-05).
 *
 * Three rows is about a third of the panel; past that the answer stops being visible,
 * which is the failure the reporter captured in the wild. The cap is here rather than
 * in the two callers so the panel and the Ask tab cannot drift — they render the same
 * component, so a change to one is a change to both.
 */
const CitationList: FC<CitationListProps> = ({
  articles,
  limit = 3,
  testId = 'citation-list',
}) => {
  const [expanded, setExpanded] = useState(false);
  const show = useCallback(() => setExpanded(true), []);

  if (articles.length === 0) {
    return null;
  }

  const visible = expanded ? articles : articles.slice(0, limit);
  const hidden = articles.length - visible.length;

  return (
    <Stack spacing={0.5} data-testid={testId}>
      {visible.map((article) => (
        <CitationCard key={article.id} articleId={article.id} />
      ))}
      {hidden > 0 ? (
        <Button
          size="small"
          variant="text"
          onClick={show}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', minHeight: 28 }}
          data-testid="citation-more"
        >
          +{hidden} more
        </Button>
      ) : null}
    </Stack>
  );
};

export default CitationList;
